/**
 * Helper functions for user management project/role filtering logic
 * Extracted from UserManagement.js for testing and reusability
 */

/**
 * Parse groups string into array of {project, role} objects
 * Format: "projectPermission:role" or "rootAccess"
 * @param {string} groupsString - Comma-separated groups
 * @returns {Array} Array of {project, role} objects
 */
export const parseGroupsIntoProjectRolePairs = (groupsString) => {
  if (!groupsString) return [];
  return groupsString
    .split(',')
    .map(group => {
      const trimmed = group.trim();
      if (trimmed === 'rootAccess') {
        return { project: 'rootAccess', role: 'rootAccess' };
      }
      const parts = trimmed.split(':');
      return {
        project: parts[0].trim(),
        role: (parts[1] || '').trim()
      };
    })
    .filter(pair => pair.project); // Remove empty entries
};

/**
 * Check if a specific (project, role) combination is already assigned
 * @param {string} groupsString - Comma-separated groups
 * @param {string} project - Project permission to check
 * @param {string} role - Role to check
 * @returns {boolean} True if the combination is already assigned
 */
export const isProjectRoleCombinationAssigned = (groupsString, project, role) => {
  if (!groupsString || !project || !role) return false;
  const pairs = parseGroupsIntoProjectRolePairs(groupsString);
  return pairs.some(pair => pair.project === project && pair.role === role);
};

/**
 * Determine whether the current caller is allowed to add/remove a specific group.
 * Mirrors the backend scoping in `assertCanAssignRole` (userManagementAuthorization.ts):
 * - `rootAccess` callers may manage any group.
 * - Global user managers may manage any `project:role` group, but never the
 *   literal `rootAccess` group or non `project:role` groups.
 * - Scoped user managers (project superadmin / userManagement) may only manage
 *   `project:role` groups whose project is in their owned project set.
 * @param {string} group - Cognito group string ("project:role" or "rootAccess")
 * @param {Object} scope - Caller scope from useGetRole
 * @param {boolean} scope.isRoot - Whether the caller has rootAccess
 * @param {boolean} scope.isGlobalUserManager - Whether the caller is a global user manager
 * @param {Array<string>} scope.ownedProjects - Projects the caller owns for user management
 * @returns {boolean} True when the caller may add/remove the group
 */
export const canCallerManageGroup = (
  group,
  { isRoot = false, isGlobalUserManager = false, ownedProjects = [] } = {},
) => {
  const trimmed = (group || '').trim();
  if (!trimmed) return false;
  // rootAccess is only manageable by another rootAccess caller.
  if (trimmed === 'rootAccess') return isRoot;
  const parts = trimmed.split(':');
  // Non `project:role` groups (e.g. autogenerated IdP groups) are not manageable
  // by scoped or global user managers; only rootAccess may touch them.
  if (parts.length !== 2 || !parts[0].trim() || !parts[1].trim()) return isRoot;
  if (isRoot || isGlobalUserManager) return true;
  return Array.isArray(ownedProjects) && ownedProjects.includes(parts[0].trim());
};

/**
 * Get all assigned projects for a user (deprecated - use parseGroupsIntoProjectRolePairs instead)
 * This function is kept for backward compatibility but only returns projects without role info
 * @param {string} groupsString - Comma-separated groups
 * @returns {Array} Array of project permissions
 */
export const getAssignedProjectsForUser = (groupsString) => {
  if (!groupsString) return [];
  return groupsString
    .split(',')
    .map(group => {
      // Format: "projectPermission:role" or "rootAccess"
      const parts = group.trim().split(':');
      return parts[0]; // Extract project permission
    })
    .filter(proj => proj && proj !== 'rootAccess'); // Exclude rootAccess and empty
};
